import { StepHeader } from "./components/StepHeader";
import React, { useEffect, useCallback } from "react";
import { useWarehouseContext } from "./WarehouseProvider";
import { FullscreenModal } from "./components/FullscreenModal";
import { useSteps } from "./useSteps";
import { useWarehouse } from "./useWarehouse";
import { OpenInNew } from "@mui/icons-material";
import { Summary } from "./Summary";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

export const AutoEscalated = () => {
  const { clearTakeback, updateTakeback } = useWarehouseContext();
  const { exit, jumpTo } = useSteps();
  const { takeback } = useWarehouse();

  // Generate a case ID if not available
  const caseId = takeback?.id;

  const handleDone = useCallback(() => {
    // Add the case ID to the notes for tracking
    const escalationNotes = `Weight escalation for case ${caseId}.`.trim();

    updateTakeback({
      isRejected: true,
      rejectReason: escalationNotes,
      rejectStatus: "escalated",
      rejectCategory: "escalated",
    });

    clearTakeback();
    exit();
  }, [caseId, updateTakeback, clearTakeback, exit]);

  useEffect(() => {
    // Add Enter key handler
    const handleKeyPress = (event) => {
      if (event.key === "Enter") {
        handleDone();
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleDone]);

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader />

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-10 max-w-[1376px] w-full px-8">
            {/* Title Section */}
            <div className="flex flex-col items-center gap-6 w-full">
              <h1 className="text-[29px] font-bold text-[#E6ECFF] tracking-[-0.01em]">
                Weight Escalation
              </h1>
            </div>

            {/* Instructions Box */}
            <div className="bg-[#030711] rounded-3xl p-8 w-full max-w-[668px]">
              <h2 className="text-xl font-bold text-[#E6ECFF] mb-6">
                Follow these instructions
              </h2>

              <div className="flex flex-col gap-4">
                {/* Instruction 1 */}
                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#173991] flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-[18px] font-bold">1</span>
                  </div>
                  <p className="text-base text-[#E6ECFF]">
                    Place conforming materials in clear labeled bag
                  </p>
                </div>

                {/* Instruction 2 */}
                <div className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#173991] flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-[18px] font-bold">2</span>
                  </div>
                  <p className="text-base text-[#E6ECFF]">
                    Place each non-conform category in separate labeled bags
                  </p>
                </div>

                {/* Instruction 3 */}
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#173991] flex-shrink-0 flex items-center justify-center">
                    <span className="text-white text-[18px] font-bold">3</span>
                  </div>
                  <p className="text-base text-[#E6ECFF]">
                    Return all bags to original tray with label and ID
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter>
          <WarehouseButtonGroup>
            <WarehouseButton variant="default" onClick={() => jumpTo(Summary)}>
              Cancel
            </WarehouseButton>
            <WarehouseButton
              variant="default"
              onClick={() =>
                window.open(
                  "https://intercom.help/legobricktakeback/en/articles/11515225-stores-with-drop-off-stations#h_8f11e2dead",
                  "_blank"
                )
              }
            >
              View Detailed Instructions
              <OpenInNew className="w-6 h-6" />
            </WarehouseButton>
            <WarehouseButton variant="primary" onClick={handleDone}>
              Ok, Done
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
    </FullscreenModal>
  );
};
